import { useNavigate } from 'react-router-dom';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Copy, FlaskConical, Globe2, ShieldCheck, type LucideIcon } from 'lucide-react';
import { platformLabel, type ClonePurpose } from '@cp/shared';
import { Banner, Button, Checkbox, Drawer, Field, Input, Select } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useNodes } from '@/hooks/useNodes';
import { useCloneSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorCode, errorField, errorText } from '@/utils/errors';
import { countryWithFlag, languageWithFlag } from '@/utils/market';
import { suggestSlug } from '@/utils/slug';
import { isDomainName, isEmail, isSlug } from '@/utils/validation';
import type { SiteDetail } from '@/types/api';

const PURPOSES: readonly { id: ClonePurpose; icon: LucideIcon; title: string; text: string }[] = [
  {
    id: 'staging',
    icon: FlaskConical,
    title: 'Тестовая копия',
    text: 'Закрыта от индексации. Для проверки изменений.',
  },
  {
    id: 'production',
    icon: Globe2,
    title: 'Новый сайт по образцу',
    text: 'Самостоятельный сайт на своём домене, открыт для индексации. Для другой страны или языка.',
  },
];

const COUNTRY = /^[A-Z]{2}$/;
const LANGUAGE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

/**
 * A copy of a site (TZ section 10.4, ADR-0038): a test copy, or a new site made
 * on its model. Only what the copy does not inherit is asked for — its name
 * and address; for a site the panel runs, its identifier and administrator;
 * for a site of an external server, the server it goes on. A new site may be
 * given the country and language it is for.
 */
export function CloneSiteDrawer({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const hosted = site.engine === 'external';

  const [purpose, setPurpose] = useState<ClonePurpose>('staging');
  const [displayName, setDisplayName] = useState(`${site.displayName} (копия)`);
  const [slug, setSlug] = useState(suggestSlug(`${site.slug}-copy`));
  const [primaryDomain, setPrimaryDomain] = useState(site.primaryDomain ? `staging.${site.primaryDomain}` : '');
  const [adminLogin, setAdminLogin] = useState('siteadmin');
  const [adminEmail, setAdminEmail] = useState('');
  const [targetNodeId, setTargetNodeId] = useState(site.hostNodeId ?? '');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  // Unsaid, it follows the purpose: a new site leaves the source's traces behind, a test copy keeps them.
  const [cleanTraces, setCleanTraces] = useState<boolean | null>(null);
  const [targetRoot, setTargetRoot] = useState('');
  const [touched, setTouched] = useState({ name: false, domain: false });
  const [shown, setShown] = useState(false);
  const key = useRef(idempotencyKey('site.clone'));

  // External servers with an agent: a copy of a site without a CMS may go on any of them.
  const nodes = useNodes({ enabled: open && hosted });
  const hosts = (nodes.data ?? []).filter((node) => node.role === 'host' && node.agentVersion !== null);

  /** A new site is not "a copy" and has no staging address: the defaults follow the choice until edited. */
  function choose(next: ClonePurpose): void {
    setPurpose(next);
    if (!touched.name) setDisplayName(next === 'staging' ? `${site.displayName} (копия)` : site.displayName);
    if (!touched.domain) setPrimaryDomain(next === 'staging' && site.primaryDomain ? `staging.${site.primaryDomain}` : '');
  }

  const problems: Partial<Record<'displayName' | 'slug' | 'primaryDomain' | 'adminEmail' | 'country' | 'language' | 'targetRoot', string>> = {};
  if (!displayName.trim()) problems.displayName = 'Нужно название';
  const domain = primaryDomain.trim().toLowerCase();
  if (!isDomainName(domain)) problems.primaryDomain = 'Не похоже на доменное имя';
  else if (domain === site.primaryDomain) problems.primaryDomain = 'У копии должен быть свой домен';
  if (!hosted) {
    if (!isSlug(slug)) problems.slug = 'Только строчная латиница, цифры и дефис; не с дефиса';
    if (!isEmail(adminEmail.trim())) problems.adminEmail = 'Нужна почта администратора';
  }
  const countryCode = country.trim().toUpperCase();
  const languageTag = language.trim();
  if (purpose === 'production' && countryCode && !COUNTRY.test(countryCode)) problems.country = 'Код страны из двух латинских букв: DE, JP, BR';
  if (purpose === 'production' && languageTag && !LANGUAGE.test(languageTag)) problems.language = 'Код языка: de, ja, pt-BR';
  const rootDir = targetRoot.trim().replace(/\/+$/, '');
  if (hosted && rootDir && (!/^\/[A-Za-z0-9._\-/]+$/.test(rootDir) || rootDir.split('/').includes('..'))) problems.targetRoot = 'Полный путь каталога: /var/www/example.de';

  const cleaning = cleanTraces ?? purpose === 'production';

  const clone = useCloneSite(site.id, {
    onSuccess: (result) => {
      toast.success('Клонирование поставлено в очередь');
      key.current = idempotencyKey('site.clone');
      onClose();
      navigate(`/sites/${result.siteId}`);
    },
  });

  const code = clone.isError ? errorCode(clone.error) : null;
  const field = clone.isError ? errorField(clone.error) : null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setShown(true);
    if (Object.keys(problems).length > 0 || clone.isPending) return;
    clone.mutate({
      input: {
        mode: purpose,
        displayName: displayName.trim(),
        primaryDomain: domain,
        ...(hosted
          ? { ...(targetNodeId && targetNodeId !== site.hostNodeId ? { targetNodeId } : {}), ...(rootDir ? { targetRoot: rootDir } : {}) }
          : { slug, adminLogin: adminLogin.trim(), adminEmail: adminEmail.trim() }),
        ...(purpose === 'production' && countryCode ? { country: countryCode } : {}),
        ...(purpose === 'production' && languageTag ? { language: languageTag } : {}),
        cleanTraces: cleaning,
      },
      key: key.current,
    });
  };

  /** The name the code stands for, as the hint under its field. */
  const known = (value: string, name: string | null, fallback: string) => (value ? (name ?? 'код не распознан — проверьте') : fallback);

  return (
    <Drawer
      open={open}
      title="Клонировать сайт"
      description={site.displayName}
      onClose={onClose}
      width="md"
      footer={
        <>
          <Button onClick={onClose} disabled={clone.isPending}>
            Отмена
          </Button>
          <Button type="submit" form="clone-site" variant="primary" icon={Copy} busy={clone.isPending}>
            Клонировать
          </Button>
        </>
      }
    >
      <form id="clone-site" className="stack" onSubmit={submit} noValidate>
        <div className="kind-choice" role="radiogroup" aria-label="Что получится">
          {PURPOSES.map(({ id, icon: Icon, title, text }) => (
            <label key={id} className={`choice${purpose === id ? ' choice-active' : ''}`}>
              <input type="radio" name="clone-purpose" value={id} checked={purpose === id} onChange={() => choose(id)} />
              <span className="stack stack-tight">
                <span className="row-tight strong">
                  <Icon size={16} aria-hidden="true" />
                  {title}
                </span>
                <span className="text-sm muted">{text}</span>
              </span>
            </label>
          ))}
        </div>

        <Field label={purpose === 'staging' ? 'Название копии' : 'Название сайта'} htmlFor="clone-name" {...(shown && problems.displayName ? { error: problems.displayName } : {})}>
          <Input
            id="clone-name"
            value={displayName}
            onChange={(event) => {
              setTouched((current) => ({ ...current, name: true }));
              setDisplayName(event.target.value);
            }}
            maxLength={160}
          />
        </Field>

        <Field
          label="Домен"
          htmlFor="clone-domain"
          {...(shown && problems.primaryDomain ? { error: problems.primaryDomain } : {})}
          {...(code === 'DOMAIN_ALREADY_BOUND' ? { error: 'Домен занят другим сайтом панели' } : {})}
        >
          <Input
            id="clone-domain"
            className="mono"
            value={primaryDomain}
            onChange={(event) => {
              setTouched((current) => ({ ...current, domain: true }));
              setPrimaryDomain(event.target.value);
            }}
            placeholder={purpose === 'production' ? 'example.de' : ''}
            spellCheck={false}
            autoComplete="off"
          />
        </Field>

        {purpose === 'production' ? (
          <div className="form-grid">
            <Field
              label="Страна"
              htmlFor="clone-country"
              hint={known(countryCode, countryWithFlag(countryCode), 'Код из двух букв: DE, JP, BR. Необязательно')}
              {...(shown && problems.country ? { error: problems.country } : {})}
            >
              <Input id="clone-country" className="mono" value={country} onChange={(event) => setCountry(event.target.value)} maxLength={2} autoComplete="off" spellCheck={false} />
            </Field>
            <Field
              label="Язык"
              htmlFor="clone-language"
              hint={known(languageTag, languageWithFlag(languageTag), 'Код: de, ja, pt-BR. Необязательно')}
              {...(shown && problems.language ? { error: problems.language } : {})}
            >
              <Input id="clone-language" className="mono" value={language} onChange={(event) => setLanguage(event.target.value)} maxLength={35} autoComplete="off" spellCheck={false} />
            </Field>
          </div>
        ) : null}

        {hosted ? (
          <Field
            label="Сервер"
            htmlFor="clone-host"
            hint="По умолчанию — сервер исходного сайта"
            {...(field === 'targetNodeId' ? { error: errorText(clone.error) } : {})}
          >
            <Select
              id="clone-host"
              label="Сервер"
              value={targetNodeId}
              onChange={setTargetNodeId}
              options={hosts.map((node) => ({ value: node.id, label: `${node.name}${node.id === site.hostNodeId ? ' — сервер исходного сайта' : ''}` }))}
            />
          </Field>
        ) : null}
        {hosted ? (
          <Field
            label="Каталог на сервере"
            htmlFor="clone-root"
            hint="Необязательно. По умолчанию — рядом с исходным сайтом"
            {...(shown && problems.targetRoot ? { error: problems.targetRoot } : {})}
            {...(field === 'targetRoot' ? { error: errorText(clone.error) } : {})}
          >
            <Input id="clone-root" className="mono" value={targetRoot} onChange={(event) => setTargetRoot(event.target.value)} placeholder={domain ? `/var/www/${domain}` : ''} spellCheck={false} autoComplete="off" />
          </Field>
        ) : (
          <>
            <Field
              label="Идентификатор"
              htmlFor="clone-slug"
              hint="Имя базы данных и контейнера"
              {...(shown && problems.slug ? { error: problems.slug } : {})}
              {...(code === 'SITE_SLUG_TAKEN' ? { error: errorText(clone.error) } : {})}
            >
              <Input id="clone-slug" className="mono" value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase())} maxLength={63} spellCheck={false} autoComplete="off" />
            </Field>
            <fieldset className="field fieldset">
              <legend className="field-label">Администратор</legend>
              <div className="form-grid">
                <Field label="Логин" htmlFor="clone-admin-login">
                  <Input id="clone-admin-login" value={adminLogin} onChange={(event) => setAdminLogin(event.target.value)} spellCheck={false} autoComplete="off" />
                </Field>
                <Field label="Почта" htmlFor="clone-admin-email" {...(shown && problems.adminEmail ? { error: problems.adminEmail } : {})}>
                  <Input id="clone-admin-email" type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} autoComplete="off" />
                </Field>
              </div>
            </fieldset>
          </>
        )}

        <Checkbox
          checked={cleaning}
          onChange={setCleanTraces}
          label="Убрать следы исходного сайта"
          hint={
            hosted
              ? 'Коды подтверждения поисковиков, счётчики аналитики, hreflang и canonical на исходный домен'
              : 'Коды подтверждения, подключения SEO- и аналитических плагинов, счётчики, canonical на исходный домен'
          }
        />

        <Banner tone="info" icon={ShieldCheck}>
          <div className="stack stack-tight">
            <span>Клон создаётся из свежей резервной копии; адреса исходного сайта заменяются на новый домен.</span>
            {hosted ? (
              <span>Сертификат сервера самоподписанный: в Cloudflare нужен режим SSL «Full».</span>
            ) : (
              <span>Сессии завершаются, пароли других администраторов сбрасываются.</span>
            )}
            {purpose === 'staging' ? (
              <span>Индексация закрыта{hosted ? '' : ', задания по расписанию отключены'}.</span>
            ) : (
              <span>
                Тексты и контакты переносятся без изменений.
                {cleaning ? ' Удалённые счётчики и коды будут указаны в журнале сайта.' : ' Счётчики и коды подтверждения исходного сайта сохранятся.'}
              </span>
            )}
            {hosted && site.platform && site.platform !== 'static' ? (
              <span>Клон копируется как файлы: {platformLabel(site.platform)} им не управляет.</span>
            ) : null}
          </div>
        </Banner>

        {clone.isError && code !== 'SITE_SLUG_TAKEN' && code !== 'DOMAIN_ALREADY_BOUND' && field !== 'targetNodeId' && field !== 'targetRoot' ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(clone.error, 'Не удалось поставить клонирование в очередь')}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
}
